import type { CloudEnvironmentDiscoverer } from '../types/CloudEnvironmentDiscoverer';
import type { CloudEnvironmentResolver } from '../types/CloudEnvironmentResolver';
import type { CloudEnvironmentStorage } from '../types/CloudEnvironmentStorage';
import type { CloudEnvironment } from '../types/CloudEnvironment';
import { getDomainFromEmailAddress } from '../utils/getDomainFromEmailAddress';

export class CloudEnvironmentResolverImpl implements CloudEnvironmentResolver {
    private readonly activeDomainResolutions = new Map<string, Promise<CloudEnvironment>>();

    constructor(
        private readonly storage: CloudEnvironmentStorage,
        private readonly discoverer: CloudEnvironmentDiscoverer
    ) {}

    async resolveConfigForEmailAddress(emailAddress: string): Promise<CloudEnvironment> {
        const domain = getDomainFromEmailAddress(emailAddress);
        if (!domain) {
            throw new Error('Invalid email address: no domain found');
        }

        const activeDomainResolution = this.activeDomainResolutions.get(domain);
        if (activeDomainResolution) {
            return activeDomainResolution;
        }

        const domainResolution = this.resolveForDomainImpl(domain).finally(() =>
            this.activeDomainResolutions.delete(domain)
        ); // always clean up active resolutions, even if they fail

        this.activeDomainResolutions.set(domain, domainResolution);
        return domainResolution;
    }

    async refreshKnownConfigs(): Promise<void> {
        const domainsToRefresh = await this.storage.getDomainsToRefresh();

        // Best-effort to refresh eligible domains; swallow any errors, they will
        // be logged within the discoverer
        await Promise.allSettled(
            domainsToRefresh.map(async domain => {
                const discovery = await this.discoverer.discoverConfigForDomain(domain);
                await this.storage.putConfigForDomain(
                    domain,
                    discovery.cloudEnvironment,
                    discovery.metadata
                );
            })
        );
    }

    private async resolveForDomainImpl(domain: string): Promise<CloudEnvironment> {
        const cachedCloudEnv = await this.storage.getConfigForDomain(domain);
        if (cachedCloudEnv) {
            return cachedCloudEnv;
        }

        const discovery = await this.discoverer.discoverConfigForDomain(domain);
        await this.storage.putConfigForDomain(
            domain,
            discovery.cloudEnvironment,
            discovery.metadata
        );

        return discovery.cloudEnvironment;
    }
}
